/**
 * ITER296 · Track B — Admin Notification Rules
 * CRUD surface for the configurable Notification Rules Engine + Digest
 * Scheduler. Replaces hardcoded email routing.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bell, Plus, Trash2, Play, Power, RefreshCw, X, Check, Clock,
  Users, Filter,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import { SectionBand } from '../components/ops/SectionBand';

const API = process.env.REACT_APP_BACKEND_URL;

const NOTIFICATION_TYPES = [
  { value: 'ticket_digest',        label: 'Ticket Digest (open + in-progress)' },
  { value: 'overdue_tickets',      label: 'Overdue Tickets (past SLA)' },
  { value: 'checklist_failures',   label: 'Checklist Failures (today)' },
  { value: 'overdue_checklists',   label: 'Overdue Checklist Submissions' },
  { value: 'repeat_failures',      label: 'Repeat Failure Patterns (30 days)' },
  { value: 'menu_item_outage',     label: 'Menu Item Outages (86\u2019d / unavailable items)' },
];

const CADENCES = [
  { value: 'hourly',      label: 'Hourly' },
  { value: 'every_6h',    label: 'Every 6 Hours' },
  { value: 'every_12h',   label: 'Every 12 Hours' },
  { value: 'daily',       label: 'Daily' },
  { value: 'weekly',      label: 'Weekly' },
];

const PRIORITIES = ['low', 'medium', 'high', 'critical'];

const WEEKDAYS = [
  { value: 0, label: 'Monday' },
  { value: 1, label: 'Tuesday' },
  { value: 2, label: 'Wednesday' },
  { value: 3, label: 'Thursday' },
  { value: 4, label: 'Friday' },
  { value: 5, label: 'Saturday' },
  { value: 6, label: 'Sunday' },
];

const EMPTY_RULE = {
  name: '',
  notification_type: 'ticket_digest',
  cadence: 'daily',
  send_time_local: '09:00',
  timezone: 'Asia/Kolkata',
  weekday: 0,
  scope: { outlet_ids: [], department: '', priorities: [] },
  recipient_user_ids: [],
  recipient_emails: [],
  enabled: true,
};

// ------------------------------------------------------------------
// Rule Editor Dialog
// ------------------------------------------------------------------
function RuleEditor({ open, onClose, initial, users, outlets, departments, onSave }) {
  const [form, setForm] = useState(EMPTY_RULE);
  const [emailInput, setEmailInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initial ? {
        ...EMPTY_RULE, ...initial,
        scope: {
          outlet_ids: initial.scope?.outlet_ids || [],
          department: initial.scope?.department || '',
          priorities: initial.scope?.priorities || [],
        },
        recipient_user_ids: initial.recipient_user_ids || [],
        recipient_emails: initial.recipient_emails || [],
      } : { ...EMPTY_RULE });
      setEmailInput('');
    }
  }, [open, initial]);

  const isEdit = !!initial?.id;

  const toggleUser = (uid) => {
    setForm(f => ({
      ...f,
      recipient_user_ids: f.recipient_user_ids.includes(uid)
        ? f.recipient_user_ids.filter(x => x !== uid)
        : [...f.recipient_user_ids, uid],
    }));
  };

  const addEmail = () => {
    const e = emailInput.trim().toLowerCase();
    if (!e || !e.includes('@')) return;
    if (form.recipient_emails.includes(e)) return;
    setForm(f => ({ ...f, recipient_emails: [...f.recipient_emails, e] }));
    setEmailInput('');
  };

  const removeEmail = (e) => {
    setForm(f => ({ ...f, recipient_emails: f.recipient_emails.filter(x => x !== e) }));
  };

  const toggleOutlet = (oid) => {
    setForm(f => ({
      ...f,
      scope: {
        ...f.scope,
        outlet_ids: f.scope.outlet_ids.includes(oid)
          ? f.scope.outlet_ids.filter(x => x !== oid)
          : [...f.scope.outlet_ids, oid],
      },
    }));
  };

  const togglePriority = (p) => {
    setForm(f => ({
      ...f,
      scope: {
        ...f.scope,
        priorities: f.scope.priorities.includes(p)
          ? f.scope.priorities.filter(x => x !== p)
          : [...f.scope.priorities, p],
      },
    }));
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error('Rule name is required');
      return;
    }
    if (form.recipient_user_ids.length === 0 && form.recipient_emails.length === 0) {
      toast.error('At least one recipient is required');
      return;
    }
    setSaving(true);
    try {
      await onSave(form, isEdit);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const showTime = ['every_12h', 'daily', 'weekly'].includes(form.cadence);
  const showWeekday = form.cadence === 'weekly';
  const isTicketType = ['ticket_digest', 'overdue_tickets'].includes(form.notification_type);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !saving && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="rule-editor-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Notification Rule' : 'New Notification Rule'}</DialogTitle>
          <DialogDescription>
            Configure what to notify about, who to notify, and how often.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Basics */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-slate-600 mb-1 block">
                Rule Name
              </Label>
              <Input
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. Daily Ops Digest — Ops Head"
                data-testid="rule-name-input"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 mb-1 block">
                Notification Type
              </Label>
              <select
                value={form.notification_type}
                onChange={e => setForm(f => ({ ...f, notification_type: e.target.value }))}
                className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                data-testid="rule-type-select"
              >
                {NOTIFICATION_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 mb-1 block">
                Cadence
              </Label>
              <select
                value={form.cadence}
                onChange={e => setForm(f => ({ ...f, cadence: e.target.value }))}
                className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                data-testid="rule-cadence-select"
              >
                {CADENCES.map(c => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            {showTime && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600 mb-1 block">
                  Send Time (Local)
                </Label>
                <Input
                  type="time"
                  value={form.send_time_local}
                  onChange={e => setForm(f => ({ ...f, send_time_local: e.target.value }))}
                  data-testid="rule-time-input"
                />
              </div>
            )}

            {showWeekday && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600 mb-1 block">
                  Day of Week
                </Label>
                <select
                  value={form.weekday}
                  onChange={e => setForm(f => ({ ...f, weekday: parseInt(e.target.value) }))}
                  className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                  data-testid="rule-weekday-select"
                >
                  {WEEKDAYS.map(d => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600 mb-1 block">
                Timezone
              </Label>
              <Input
                value={form.timezone}
                onChange={e => setForm(f => ({ ...f, timezone: e.target.value }))}
                placeholder="Asia/Kolkata"
                data-testid="rule-timezone-input"
              />
            </div>
          </div>

          {/* Scope */}
          <div className="border-t pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="w-4 h-4 text-slate-500" />
              <h4 className="text-sm font-semibold text-slate-700">Filters (optional)</h4>
            </div>

            <div className="mb-3">
              <Label className="text-xs uppercase tracking-wider text-slate-600 mb-1 block">
                Outlets ({form.scope.outlet_ids.length === 0 ? 'All' : form.scope.outlet_ids.length})
              </Label>
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border rounded-md bg-slate-50">
                {outlets.map(o => {
                  const active = form.scope.outlet_ids.includes(o.id);
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => toggleOutlet(o.id)}
                      className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                        active
                          ? 'bg-slate-800 text-white border-slate-800'
                          : 'bg-white text-slate-700 hover:border-slate-400'
                      }`}
                      data-testid={`rule-outlet-chip-${o.id}`}
                    >
                      {o.name}
                    </button>
                  );
                })}
                {outlets.length === 0 && (
                  <span className="text-xs text-slate-500 italic">No outlets available</span>
                )}
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                Leave empty to include all outlets.
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600 mb-1 block">
                  Department
                </Label>
                <select
                  value={form.scope.department || ''}
                  onChange={e => setForm(f => ({
                    ...f, scope: { ...f.scope, department: e.target.value },
                  }))}
                  className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                  data-testid="rule-department-select"
                >
                  <option value="">All Departments</option>
                  {departments.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>

              {isTicketType && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-600 mb-1 block">
                    Priorities
                  </Label>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {PRIORITIES.map(p => {
                      const active = form.scope.priorities.includes(p);
                      return (
                        <button
                          key={p}
                          type="button"
                          onClick={() => togglePriority(p)}
                          className={`px-2.5 py-1 text-xs rounded-full border capitalize transition-colors ${
                            active
                              ? 'bg-slate-800 text-white border-slate-800'
                              : 'bg-white text-slate-700 hover:border-slate-400'
                          }`}
                          data-testid={`rule-priority-chip-${p}`}
                        >
                          {p}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Recipients */}
          <div className="border-t pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-slate-500" />
              <h4 className="text-sm font-semibold text-slate-700">
                Recipients ({form.recipient_user_ids.length + form.recipient_emails.length})
              </h4>
            </div>

            <Label className="text-xs uppercase tracking-wider text-slate-600 mb-1 block">
              Pick Users
            </Label>
            <div className="max-h-40 overflow-y-auto border rounded-md bg-slate-50 p-2 space-y-1">
              {users.map(u => {
                const active = form.recipient_user_ids.includes(u.id);
                return (
                  <label
                    key={u.id}
                    className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-xs ${
                      active ? 'bg-slate-800 text-white' : 'bg-white hover:bg-slate-100'
                    }`}
                    data-testid={`rule-user-picker-${u.id}`}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => toggleUser(u.id)}
                      className="accent-slate-800"
                    />
                    <span className="flex-1">
                      <span className="font-medium">{u.name || u.email}</span>
                      <span className={`ml-2 ${active ? 'text-slate-300' : 'text-slate-500'}`}>
                        · {u.role}{u.outlet_name ? ` · ${u.outlet_name}` : ''}
                      </span>
                    </span>
                    <span className={`text-[10px] ${active ? 'text-slate-300' : 'text-slate-500'}`}>
                      {u.email}
                    </span>
                  </label>
                );
              })}
              {users.length === 0 && (
                <div className="text-xs text-slate-500 italic p-2">No users available</div>
              )}
            </div>

            <div className="mt-3">
              <Label className="text-xs uppercase tracking-wider text-slate-600 mb-1 block">
                Additional Emails
              </Label>
              <div className="flex gap-2">
                <Input
                  value={emailInput}
                  onChange={e => setEmailInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
                  placeholder="email@example.com"
                  data-testid="rule-email-input"
                />
                <Button type="button" onClick={addEmail} variant="outline"
                        data-testid="rule-email-add">
                  Add
                </Button>
              </div>
              {form.recipient_emails.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.recipient_emails.map(e => (
                    <span key={e}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs
                                     bg-slate-100 rounded-full"
                          data-testid={`rule-email-chip-${e}`}>
                      {e}
                      <button type="button" onClick={() => removeEmail(e)}
                              className="hover:text-red-600">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Enabled */}
          <div className="border-t pt-4 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-slate-700">Enabled</div>
              <div className="text-xs text-slate-500">
                Disabled rules don&apos;t fire on the schedule. Manual &quot;Run now&quot; still works.
              </div>
            </div>
            <Switch
              checked={form.enabled}
              onCheckedChange={v => setForm(f => ({ ...f, enabled: v }))}
              data-testid="rule-enabled-switch"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}
                  data-testid="rule-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="rule-save">
            {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Rule')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------------------------------------------
// Main page
// ------------------------------------------------------------------
export default function AdminNotificationRules() {
  const { user } = useAuth();
  const [rules, setRules] = useState([]);
  const [users, setUsers] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [runningId, setRunningId] = useState(null);

  const token = useMemo(() => localStorage.getItem('token'), []);
  const authCfg = useMemo(() => ({
    headers: { Authorization: `Bearer ${token}` },
  }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesR, usersR, outletsR, deptsR] = await Promise.all([
        axios.get(`${API}/api/admin/notification-rules`, authCfg),
        axios.get(`${API}/api/admin/notification-rules/recipient-candidates`, authCfg),
        axios.get(`${API}/api/outlets`, authCfg),
        axios.get(`${API}/api/admin/departments`, authCfg),
      ]);
      setRules(rulesR.data || []);
      setUsers(usersR.data || []);
      setOutlets(outletsR.data || []);
      setDepartments((deptsR.data?.departments) || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load notification rules');
    } finally {
      setLoading(false);
    }
  }, [authCfg]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = (r) => { setEditing(r); setEditorOpen(true); };

  const saveRule = async (form, isEdit) => {
    if (isEdit) {
      await axios.patch(
        `${API}/api/admin/notification-rules/${editing.id}`, form, authCfg,
      );
      toast.success('Rule updated');
    } else {
      await axios.post(`${API}/api/admin/notification-rules`, form, authCfg);
      toast.success('Rule created');
    }
    await load();
  };

  const toggleEnabled = async (rule) => {
    try {
      await axios.patch(
        `${API}/api/admin/notification-rules/${rule.id}`,
        { enabled: !rule.enabled }, authCfg,
      );
      toast.success(rule.enabled ? 'Rule disabled' : 'Rule enabled');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to toggle rule');
    }
  };

  const deleteRule = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.name}"?`)) return;
    try {
      await axios.delete(`${API}/api/admin/notification-rules/${rule.id}`, authCfg);
      toast.success('Rule deleted');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete rule');
    }
  };

  const runNow = async (rule) => {
    setRunningId(rule.id);
    try {
      const r = await axios.post(
        `${API}/api/admin/notification-rules/${rule.id}/run-now`, {}, authCfg,
      );
      const entry = r.data || {};
      if (entry.status === 'sent') {
        toast.success(`Digest sent to ${(entry.recipients || []).length} recipient(s) — ${entry.item_count} item(s)`);
      } else if (entry.status === 'skipped') {
        toast.warning(`Skipped: ${entry.error || 'no reason'}`);
      } else {
        toast.error(`Failed: ${entry.error || 'unknown'}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to run rule');
    } finally {
      setRunningId(null);
    }
  };

  const typeLabel = (t) => NOTIFICATION_TYPES.find(x => x.value === t)?.label || t;
  const cadenceLabel = (c) => CADENCES.find(x => x.value === c)?.label || c;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="notification-rules-page">
      <SectionBand
        title="Notification Rules"
        subtitle="Configure every operational digest — tickets, checklist failures, repeat patterns. Cadence-based sending replaces hardcoded email routing."
        icon={Bell}
        testId="notification-rules-page-band"
        actions={
          <>
            <Button variant="ghost" size="sm" onClick={load} disabled={loading}
                    className="text-white/90 hover:text-white hover:bg-white/10"
                    data-testid="refresh-rules">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button size="sm" onClick={openNew}
                    className="bg-white text-[color:var(--color-primary)] hover:bg-white/90"
                    data-testid="new-rule-btn">
              <Plus className="w-4 h-4 mr-1" />
              New Rule
            </Button>
          </>
        }
      />

      {loading && rules.length === 0 ? (
        <div className="text-center py-16 text-slate-500 text-sm">Loading rules…</div>
      ) : rules.length === 0 ? (
        <div className="text-center py-16 bg-white border rounded-lg" data-testid="empty-state">
          <Bell className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="text-slate-700 font-semibold">No notification rules yet</div>
          <div className="text-slate-500 text-sm mt-1 max-w-md mx-auto">
            Create your first rule to route operational digests to the right people on your cadence.
          </div>
          <Button onClick={openNew} className="mt-4" data-testid="empty-new-rule">
            <Plus className="w-4 h-4 mr-2" />
            Create First Rule
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {rules.map(r => (
            <RuleCard
              key={r.id}
              rule={r}
              typeLabel={typeLabel}
              cadenceLabel={cadenceLabel}
              onEdit={() => openEdit(r)}
              onDelete={() => deleteRule(r)}
              onToggle={() => toggleEnabled(r)}
              onRun={() => runNow(r)}
              running={runningId === r.id}
            />
          ))}
        </div>
      )}

      <RuleEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        initial={editing}
        users={users}
        outlets={outlets}
        departments={departments}
        onSave={saveRule}
      />
    </div>
  );
}

function RuleCard({ rule, typeLabel, cadenceLabel, onEdit, onDelete, onToggle, onRun, running }) {
  const recipientCount = (rule.recipient_user_ids?.length || 0) + (rule.recipient_emails?.length || 0);
  const scopeSummary = [];
  if (rule.scope?.outlet_ids?.length) scopeSummary.push(`${rule.scope.outlet_ids.length} outlet${rule.scope.outlet_ids.length !== 1 ? 's' : ''}`);
  if (rule.scope?.department) scopeSummary.push(rule.scope.department);
  if (rule.scope?.priorities?.length) scopeSummary.push(rule.scope.priorities.join('/'));
  const lastRunLabel = rule.last_run_at
    ? new Date(rule.last_run_at).toLocaleString()
    : 'Never';
  const statusColour = {
    sent:    { bg: '#DCFCE7', fg: '#166534' },
    skipped: { bg: '#FEF3C7', fg: '#92400E' },
    failed:  { bg: '#FEE2E2', fg: '#991B1B' },
  }[rule.last_run_status] || { bg: '#F1F5F9', fg: '#64748B' };

  return (
    <div
      className={`bg-white border rounded-lg p-4 transition-colors ${
        rule.enabled ? 'border-slate-200 hover:border-slate-400' : 'border-dashed border-slate-300 bg-slate-50'
      }`}
      data-testid={`rule-card-${rule.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3
              className="text-base font-semibold text-slate-900 cursor-pointer hover:underline"
              onClick={onEdit}
              data-testid={`rule-name-${rule.id}`}
            >
              {rule.name}
            </h3>
            {!rule.enabled && (
              <span className="text-[10px] uppercase tracking-wider bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full">
                Disabled
              </span>
            )}
          </div>
          <div className="text-xs text-slate-600 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-medium">{typeLabel(rule.notification_type)}</span>
            <span className="text-slate-400">·</span>
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {cadenceLabel(rule.cadence)}
              {['daily', 'weekly', 'every_12h'].includes(rule.cadence) && ` @ ${rule.send_time_local}`}
              {rule.cadence === 'weekly' && ` · ${WEEKDAYS[rule.weekday]?.label}`}
            </span>
            <span className="text-slate-400">·</span>
            <span className="inline-flex items-center gap-1">
              <Users className="w-3 h-3" />
              {recipientCount} recipient{recipientCount !== 1 ? 's' : ''}
            </span>
            {scopeSummary.length > 0 && (
              <>
                <span className="text-slate-400">·</span>
                <span className="inline-flex items-center gap-1">
                  <Filter className="w-3 h-3" />
                  {scopeSummary.join(' / ')}
                </span>
              </>
            )}
          </div>
          <div className="text-[11px] text-slate-500 mt-2 flex items-center gap-2">
            Last run: <span className="text-slate-700">{lastRunLabel}</span>
            {rule.last_run_status && (
              <span
                className="px-1.5 py-0.5 rounded text-[10px] uppercase font-semibold tracking-wider"
                style={{ background: statusColour.bg, color: statusColour.fg }}
              >
                {rule.last_run_status}{rule.last_run_count != null ? ` · ${rule.last_run_count}` : ''}
              </span>
            )}
            {rule.last_run_error && (
              <span className="text-red-600 text-[10px] truncate max-w-md">
                {rule.last_run_error}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={onRun}
            disabled={running}
            data-testid={`run-now-${rule.id}`}
            title="Run now"
          >
            <Play className={`w-3.5 h-3.5 ${running ? 'animate-pulse' : ''}`} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onToggle}
            data-testid={`toggle-rule-${rule.id}`}
            title={rule.enabled ? 'Disable' : 'Enable'}
          >
            <Power className={`w-3.5 h-3.5 ${rule.enabled ? 'text-emerald-600' : 'text-slate-400'}`} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onEdit}
            data-testid={`edit-rule-${rule.id}`}
          >
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onDelete}
            data-testid={`delete-rule-${rule.id}`}
            title="Delete"
          >
            <Trash2 className="w-3.5 h-3.5 text-red-600" />
          </Button>
        </div>
      </div>
    </div>
  );
}
